/**
 * Keystore — gerencia a owner key da GoyazAccount no browser.
 *
 * Operações:
 *   - create(password): gera ECDSA secp256k1, criptografa com senha, salva
 *   - unlock(password): carrega e descriptografa em memória
 *   - lock(): limpa a private key da RAM
 *   - exportPrivateKey(password): mostra hex pra usuário backupar
 *   - importPrivateKey(hex, password): se usuário já tem chave (raro)
 *
 * A private key descriptografada SÓ EXISTE durante a sessão (na memória
 * deste módulo). Nunca trafega pelo backend, nunca vai pro logging.
 */
import { generatePrivateKey, privateKeyToAccount } from "viem/accounts";
import { type Hex, hexToBytes, bytesToHex } from "viem";
import { encryptWithPassword, decryptWithPassword } from "./crypto";
import { saveKey, loadKey, deleteKey, type StoredKey } from "./storage";

/**
 * Blob cifrado serializado em base64 — formato trafegado pro/do backend
 * (key vault). O backend trata como opaco; só o navegador, com a senha,
 * consegue abrir.
 */
export interface VaultBlob {
  ciphertext: string;
  salt: string;
  iv: string;
  address: string;
}

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

class Keystore {
  private privateKey: Hex | null = null;
  private address: string | null = null;

  /**
   * Cria nova owner key + GoyazAccount address-de-domínio.
   * Retorna o endereço pra que o frontend possa registrar com o backend.
   */
  async create(password: string): Promise<{ address: string }> {
    const priv = generatePrivateKey();
    const account = privateKeyToAccount(priv);

    const blob = await encryptWithPassword(hexToBytes(priv), password);
    const stored: StoredKey = {
      ciphertext: blob.ciphertext,
      salt: blob.salt,
      iv: blob.iv,
      address: account.address,
      createdAt: Date.now(),
    };
    await saveKey(stored);

    this.privateKey = priv;
    this.address = account.address;

    return { address: account.address };
  }

  async unlock(password: string): Promise<{ address: string }> {
    const stored = await loadKey();
    if (!stored) throw new Error("Sem keystore. Crie uma conta primeiro.");

    const bytes = await decryptWithPassword(
      { ciphertext: stored.ciphertext, salt: stored.salt, iv: stored.iv },
      password,
    );
    const priv = bytesToHex(bytes) as Hex;

    // Confirma que descriptografou a chave certa
    const account = privateKeyToAccount(priv);
    if (account.address.toLowerCase() !== stored.address.toLowerCase()) {
      throw new Error("Inconsistência: chave descriptografada não bate com address.");
    }

    this.privateKey = priv;
    this.address = stored.address;
    return { address: stored.address };
  }

  lock(): void {
    this.privateKey = null;
    this.address = null;
  }

  isUnlocked(): boolean {
    return this.privateKey !== null;
  }

  getAddress(): string | null {
    return this.address;
  }

  /**
   * Retorna a private key em hex pra usuário exportar pra MetaMask.
   * Exige a senha como confirmação (não usa a chave em memória).
   */
  async exportPrivateKey(password: string): Promise<Hex> {
    const stored = await loadKey();
    if (!stored) throw new Error("Sem keystore");
    const bytes = await decryptWithPassword(
      { ciphertext: stored.ciphertext, salt: stored.salt, iv: stored.iv },
      password,
    );
    return bytesToHex(bytes) as Hex;
  }

  /**
   * Acesso interno usado pelo SDK AA pra assinar UserOps.
   * Throw se locked.
   */
  getPrivateKey(): Hex {
    if (!this.privateKey) throw new Error("Keystore travado — faça login primeiro");
    return this.privateKey;
  }

  /**
   * Assina dados EIP-712 (voto/delegação de governança) com a chave local.
   * A chave nunca sai do navegador — sai só a assinatura.
   */
  async signTypedData(params: {
    domain: Record<string, unknown>;
    types: Record<string, { name: string; type: string }[]>;
    primaryType: string;
    message: Record<string, unknown>;
  }): Promise<Hex> {
    if (!this.privateKey) throw new Error("Keystore travado — faça login primeiro");
    const account = privateKeyToAccount(this.privateKey);
    // viem valida os tipos em runtime; o cast mantém a API flexível aqui.
    return account.signTypedData(params as Parameters<typeof account.signTypedData>[0]);
  }

  async destroy(): Promise<void> {
    this.lock();
    await deleteKey();
  }

  /**
   * Serializa o blob cifrado local em base64 pra enviar ao key vault do
   * backend. Não descriptografa nada — o que sai daqui já está cifrado
   * com a senha do usuário.
   */
  async exportVault(): Promise<VaultBlob | null> {
    const stored = await loadKey();
    if (!stored) return null;
    return {
      ciphertext: bytesToB64(stored.ciphertext),
      salt: bytesToB64(stored.salt),
      iv: bytesToB64(stored.iv),
      address: stored.address,
    };
  }

  /**
   * Restaura a chave a partir de um blob baixado do vault, validando a
   * senha (descriptografa) e conferindo que o endereço bate. Salva local
   * e deixa a chave desbloqueada. Usado na recuperação em outro device.
   */
  async restoreFromVault(vault: VaultBlob, password: string): Promise<{ address: string }> {
    const blob = {
      ciphertext: b64ToBytes(vault.ciphertext),
      salt: b64ToBytes(vault.salt),
      iv: b64ToBytes(vault.iv),
    };
    const bytes = await decryptWithPassword(blob, password); // throw InvalidPasswordError se senha errada
    const priv = bytesToHex(bytes) as Hex;

    const account = privateKeyToAccount(priv);
    if (account.address.toLowerCase() !== vault.address.toLowerCase()) {
      throw new Error("Chave do backup não corresponde ao endereço registrado.");
    }

    const stored: StoredKey = {
      ciphertext: blob.ciphertext,
      salt: blob.salt,
      iv: blob.iv,
      address: vault.address,
      createdAt: Date.now(),
    };
    await saveKey(stored);

    this.privateKey = priv;
    this.address = vault.address;
    return { address: vault.address };
  }
}

export const keystore = new Keystore();
